<template>
  <v-container fluid :class="currentSession == null ? 'fill-height' : ''">
    <v-row v-if="currentSession == null">
      <v-col>
        <Loading />
      </v-col>
    </v-row>

    <v-row align="center" v-else>
      <v-col cols="12">
        <v-breadcrumbs :items="breadCrumbItems" large>
          <template v-slot:item="{ item }">
            <v-breadcrumbs-item
              :disabled="item.disabled"
              :class="[
                'text-capitalize px-0',
                {
                  'cursor-pointer':
                    item.routeName != $route.name && !item.disabled,
                },
              ]"
              @click="handleRoute(item)"
            >
              <div
                :class="[
                  item.routeName != $route.name && !item.disabled
                    ? 'primary--text'
                    : 'grey--text',
                  'text-subtitle ',
                ]"
              >
                {{ item.text }}
              </div>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
      </v-col>
      <v-col cols="12">
        <v-card class="pl-12 py-5 pr-10" outlined>
          <v-card-title v-if="isEditMode">{{
            currentSession.name
          }}</v-card-title>
          <v-card-title v-else>Add a session</v-card-title>
          <v-card-text>
            <!---Start: Show Stepper while creating a session--->
            <v-stepper
              v-model="currentStep"
              alt-labels
              class="elevation-0"
              v-if="!isEditMode"
            >
              <!--Start: Stepper Header--->
              <v-stepper-header class="elevation-0">
                <v-stepper-step
                  :complete="1 < currentStep"
                  key="`subEventStep1`"
                  :class="['text-no-wrap']"
                  :step="1"
                  color="primary lighten-1"
                  error-icon="mdi-alert-circle"
                >
                  <span>Session details</span>
                </v-stepper-step>
                <v-divider :key="`subEventStep1Divider`"></v-divider>
                <v-stepper-step
                  :complete="2 < currentStep"
                  key="`subEventStep2`"
                  :class="['text-no-wrap']"
                  :step="2"
                  color="primary lighten-1"
                  error-icon="mdi-alert-circle"
                >
                  <span>Speakers</span>
                </v-stepper-step>
              </v-stepper-header>
              <!--End: Stepper Header--->

              <v-stepper-items style="overflow: visible">
                <v-stepper-content step="1">
                  <AddEditSessionDetails
                    ref="addEditSessionDetailsComponentRef"
                  />
                </v-stepper-content>
                <v-stepper-content step="2">
                  <AddEditSpeaker />
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
            <!---End: Show Stepper while creating a session--->

            <!---Start: Show Tabs while creating a session--->
            <template v-else>
              <v-tabs
                v-model="currentSession.currentTab"
                active-class="primary--text"
              >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Session details</v-tab
                >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Speakers</v-tab
                >
              </v-tabs>
              <v-divider></v-divider>
              <v-tabs-items v-model="currentSession.currentTab">
                <v-tab-item>
                  <AddEditSessionDetails
                    ref="addEditSessionDetailsComponentRef"
                /></v-tab-item>
                <v-tab-item> <AddEditSpeaker /></v-tab-item>
              </v-tabs-items>
            </template>
            <!---End: Show Tabs while creating a session--->
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
              <v-col cols="12" class="d-flex justify-start">
                <v-btn
                  width="150"
                  color="grey lighten-4"
                  depressed
                  class="text-body-1 mr-4"
                  large
                  v-if="!isEditMode"
                  @click="handleBack"
                  >{{ backButtonText }}</v-btn
                >
                <v-btn
                  v-if="showSaveBtn"
                  width="150"
                  color="primary"
                  depressed
                  class="text-body-1"
                  @click="handleNext"
                  large
                  >{{ saveBtnText }}</v-btn
                >
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Loading from "@/components/Loading";
import { mapGetters, mapActions } from "vuex";
import AddEditSessionDetails from "@/components/MultiConnect/Session/AddEditSessionDetails";
import AddEditSpeaker from "@/components/MultiConnect/Session/AddEditSpeaker";
import constants from "@/shared/constants";
import Event from "@/models/Event";
import {eventDocRef} from "@/shared/firebase_utils";
import {onSnapshot} from "firebase/firestore";

export default {
  components: {
    Loading,
    AddEditSessionDetails,
    AddEditSpeaker,
  },
  computed: {
    ...mapGetters([
      "currentSubEvent",
      "userProfile",
      "selectedBusinessProfileId",
      "businessProfile",
      "currentEvent",
      "currentSession",
    ]),

    currentStep() {
      let currentStepItem = this.stepper.find((step) => {
        return step.stepName == this.currentSession.currentStep;
      });
      if (currentStepItem) return currentStepItem.step;
      else return 1;
    },
    isEditMode() {
      if (this.currentSession)
        return !this.currentSession?.isCreationInProgress;
      else return true;
    },
    showSaveBtn() {
      if (this.isEditMode && this.currentSession.currentTab != 0) return false;
      else return true;
    },
    saveBtnText() {
      if (this.isEditMode) return "Save";
      else {
        if (this.currentSession.currentStep == constants.SESSION_SPEAKER)
          return "Done";
        else return "Next";
      }
    },
    backButtonText() {
      if (this.currentStep == 1) return "Cancel";
      else return "Back";
    },
    breadCrumbItems() {
      let currentRouteName = this.isEditMode
        ? this.currentSession.name
        : "Add a session";

      if (this.currentEvent && this.currentSubEvent && this.currentSession) {
        let breadCrumbItems = [
          {
            text: "EventConnect",
            disabled: false,
            routeName: "MultiConnect",
            params: "",
          },
          {
            text: this.currentEvent.name,
            disabled: false,
            routeName: "editMultiConnect",
            params: { eventId: this.currentEvent.id },
          },
          {
            text: this.currentSubEvent.name,
            disabled: false,
            routeName: "addEditSubEvent",
            params: {
              eventId: this.currentEvent.id,
              subEventId: this.currentSubEvent.id,
            },
          },
          {
            text: currentRouteName,
            disabled: true,
            routeName: "addEditSession",
            params: {
              eventId: this.currentEvent.id,
              subEventId: this.currentSubEvent.id,
              sessionId: this.currentSession.id,
            },
          },
        ];
        return breadCrumbItems;
      } else return [];
    },
  },

  data() {
    return {
      stepper: [
        {
          step: 1,
          stepName: "EVENT_DETAILS",
        },
        {
          step: 2,
          stepName: "SESSION_SPEAKER",
        },
      ],
      unsubscribeEventsSnapshot: null,
      unsubscribeSubEventsSnapshot: null,
      unsubscribeSessionSnapshot: null,
    };
  },
  created() {
    this.eventId = this.$route.params.eventId;
    this.subEventId = this.$route.params.subEventId;
    this.sessionId = this.$route.params.sessionId;

    //Get event doc from firestore

    this.unsubscribeEventsSnapshot = onSnapshot(
          eventDocRef(this.eventId),
          async (documentSnapshot) => {
        //Event exists in firestore
        if (documentSnapshot.data() != undefined) {
          let newEvent = Event.fromFireStore(documentSnapshot);

          // Update the current tab
          if (this.currentEvent != null) {
            newEvent.currentTab = this.currentEvent.currentTab;
          }

          await this.setCurrentEvent(newEvent);
        } else {
          console.log("event document does not exist in Fs");
        }
      });

    //Get sub-event doc from Firestore
    this.unsubscribeSubEventsSnapshot = onSnapshot(
          eventDocRef(this.subEventId),
          async (documentSnapshot) => {
        //Sub-Event exists in firestore
        if (documentSnapshot.data() != undefined) {
          console.log("event is defined");
          let newSubEvent = Event.fromFireStore(documentSnapshot);

          // Update the current tab
          if (this.currentSubEvent != null) {
            newSubEvent.currentTab = this.currentSubEvent.currentTab;
          }

          await this.setCurrentSubEvent(newSubEvent);
        } else {
          console.log("sub-event document does not exist in Fs");

          // let subEvent = new Event({
          //   id: this.subEventId,
          //   businessProfileId: this.selectedBusinessProfileId,
          //   hostName: this.businessProfile.businessName,
          //   isCreationInProgress: true,
          //   parentMultiConnectEventId: this.currentEvent.id,
          //   currentStep: "EVENT_DETAILS",
          // });

          // this.setCurrentSubEvent(subEvent);
        }
      });

    //Get session doc from Firestore
    this.unsubscribeSessionSnapshot = onSnapshot(
          eventDocRef(this.sessionId),
          async (documentSnapshot) => {
        //Sub-Event exists in firestore
        if (documentSnapshot.data() != undefined) {
          console.log("session is defined");
          let newSession = Event.fromFireStore(documentSnapshot);

          // Update the current tab
          if (this.currentSession != null) {
            newSession.currentTab = this.currentSession.currentTab;
          }

          await this.setCurrentSession(newSession);
        } else {
          console.log("session document does not exist in Fs");

          let session = new Event({
            id: this.sessionId,
            businessProfileId: this.selectedBusinessProfileId,
            hostName: this.businessProfile.businessName,
            isCreationInProgress: true,
            parentMultiConnectEventId: this.currentEvent.id,
            parentSubEventId: this.currentSubEvent.id,
            currentStep: "EVENT_DETAILS",
            linkPrivacy: this.currentEvent.linkPrivacy,
            eventCategory: constants.EVENT_CATEGORY.session,
            startDateTime: this.currentSubEvent.startDateTime,
            endDateTime: this.currentSubEvent.endDateTime,
          });

          this.setCurrentSession(session);
        }
      });
  },
  destroyed() {
    this.unsubscribeEventsSnapshot();
    this.clearCurrentEvent();
    this.unsubscribeSubEventsSnapshot();
    this.clearCurrentSubEvent();
    this.unsubscribeSessionSnapshot();
    this.clearCurrentSession();
  },
  methods: {
    ...mapActions([
      "setCurrentEvent",
      "initializeEvent",
      "clearCurrentEvent",
      "setCurrentSubEvent",
      "clearCurrentSubEvent",
      "setCurrentSession",
      "clearCurrentSession",
      "showSnackbar",
    ]),
    handleNext() {
      console.log(
        "this.currentSession.startDateTime in handle next: ",
        this.currentSession.startDateTime
      );
      console.log(
        "this.currentSession.endDateTime  in handle next: ",
        this.currentSession.endDateTime
      );
      //Start: Form validation
      let isValid;
      if (this.currentStep == 1)
        isValid = this.$refs.addEditSessionDetailsComponentRef.validatePage();
      else isValid = true;
      //End: Form validation

      if (isValid) {
        let isLastStep = false;
        if (!this.isEditMode) {
          if (this.currentSession.currentStep == constants.EVENT_DETAILS_PAGE) {
            this.currentSession.currentStep = constants.SESSION_SPEAKER;
          } else if (
            this.currentSession.currentStep == constants.SESSION_SPEAKER
          ) {
            this.currentSession.isCreationInProgress = false;
            isLastStep = true;
          }

          let sessionIndex = this.currentSubEvent.linkedSessions.findIndex(
            (sessionId) => sessionId == this.currentSession.id
          );
          //Add the session id to current sub-event
          if (sessionIndex == -1)
            this.currentSubEvent.linkedSessions.push(this.sessionId);
        }
        //
        console.log("this.currentSession: ", this.currentSession);

        //Save the event and sub-event
        this.currentSession.save();
        this.currentSubEvent.save();
        this.currentEvent.save();

        if (this.isEditMode) {
          this.showSnackbar({ show: true, text: "Saved successfully!" });
        }

        //Show snackbar if user is on last step of stepper while creating session or
        if (isLastStep || this.isEditMode)
          this.showSnackbar({ show: true, text: "Saved successfully!" });

        if (isLastStep) {
          //Navigate to event program table
          this.$router.push({
            name: "addEditSubEvent",
            params: {
              eventId: this.currentEvent.id,
              subEventId: this.currentSubEvent.id,
            },
          });
        }
      } else {
        console.log("Form is invalid, please try again");
      }
    },
    handleRoute(item) {
      if (!item.disabled) {
        this.$router.push({ name: item.routeName, params: item.params });
      }
    },
    handleBack() {
      if (this.currentStep == 1)
        this.$router.push({
          name: "addEditSubEvent",
          params: {
            eventId: this.currentEvent.id,
            subEventId: this.currentSubEvent.id,
          },
        });
      else {
        this.currentSession.currentStep = constants.EVENT_DETAILS_PAGE;
        this.currentSession.save();
      }
    },
  },
};
</script>

<style>
</style>